/**
 * Promotion Logic Utilities
 * 
 * Centralized utilities for handling product promotion detection and logic
 * across the Wennze marketplace frontend.
 * 
 * @author GitHub Copilot
 * @date September 4, 2025
 */

/**
 * Product promotion detection utility
 * Handles all the complex logic for determining if a product is promoted
 * 
 * @param {Object} product - The product object to check
 * @returns {boolean} - True if the product is prexport {
  isProductPromoted,
  filterPromotedProducts,
  filterNonPromotedProducts,
  filterAllPromotedProducts,
  getPromotionTypeLabel,
  getPromotionBadgeClass,
  hasActivePromotion,
  sortPromotedFirst,
  getPromotionPriority,
  sortByPromotionPriority,
  enhanceProductWithPromotion,
  enhanceProductsWithPromotion,
  isPromotionExpired,
  getPromotionTimeRemaining,
  filterActivePromotions,
  getPromotionStatus,
};erwise
 */
export const isProductPromoted = (product) => {
  // Handle null/undefined products
  if (!product) return false;

  // First check if the backend provides is_promotion_active (recommended)
  if (typeof product.is_promotion_active === 'boolean') {
    return product.is_promotion_active;
  }

  // Support alternative boolean flags sent by different APIs
  if (typeof product.has_active_promotion === 'boolean') {
    return product.has_active_promotion;
  }

  if (typeof product.is_promoted === 'boolean') {
    return product.is_promoted;
  }

  // Fallback: Enhanced promotion detection - check multiple possible fields
  // This covers all the different ways promotion status can be represented
  const hasPromotionFields = Boolean(
    product.promote_type && 
    product.promote_type !== null && 
    product.promote_type !== '' && 
    product.promote_type !== 'none'
  );

  if (!hasPromotionFields) return false;

  // Check expiration if dates are available
  if (product.promoted_end_date) {
    const endDate = new Date(product.promoted_end_date);
    const now = new Date();
    
    // Check if promotion has ended
    if (now > endDate) return false;

    // Check if promotion has started (if start date exists)
    if (product.promoted_start_date) {
      const startDate = new Date(product.promoted_start_date);
      if (now < startDate) return false;
    }
  }

  return hasPromotionFields;
};

/**
 * Filter products to show only promoted ones that are still active
 * 
 * @param {Array} products - Array of product objects
 * @returns {Array} - Array containing only actively promoted products
 */
export const filterPromotedProducts = (products) => {
  if (!Array.isArray(products)) return [];
  
  return products.filter(product => {
    // Must have promotion type
    if (!product.promote_type || 
        product.promote_type === 'none' || 
        product.promote_type === null) {
      return false;
    }
    
    // Must be an active promotion (not expired)
    return hasActivePromotion(product) && !isPromotionExpired(product);
  });
};

/**
 * Filter products to show only non-promoted ones
 * 
 * @param {Array} products - Array of product objects
 * @returns {Array} - Array containing only non-promoted products
 */
export const filterNonPromotedProducts = (products) => {
  if (!Array.isArray(products)) return [];
  
  return products.filter(product => 
    !product.promote_type || 
    product.promote_type === 'none' || 
    product.promote_type === null
  );
};

/**
 * Filter products to show all that have any promotion type (including expired)
 * This is for admin/debugging purposes only
 * 
 * @param {Array} products - Array of product objects
 * @returns {Array} - Array containing all products with promotion type
 */
export const filterAllPromotedProducts = (products) => {
  if (!Array.isArray(products)) return [];
  
  return products.filter(product => 
    product.promote_type && 
    product.promote_type !== 'none' && 
    product.promote_type !== null
  );
};

/**
 * Get promotion type label for display
 * 
 * @param {Object} product - The product object
 * @returns {string} - Human-readable promotion type label
 */
export const getPromotionTypeLabel = (product) => {
  if (!isProductPromoted(product)) return '';

  const promoteType = product.promote_type;
  
  switch (promoteType) {
    case 'premium':
      return 'SPONSORISÉ';
    case 'standard':
      return 'SPONSORISÉ';
    case 'basic':
      return 'SPONSORISÉ';
    default:
      return 'SPONSORISÉ';
  }
};

/**
 * Get promotion badge CSS class
 * 
 * @param {Object} product - The product object
 * @returns {string} - CSS class name for promotion badge
 */
export const getPromotionBadgeClass = (product) => {
  if (!isProductPromoted(product)) return '';

  const promoteType = product.promote_type;
  
  switch (promoteType) {
    case 'premium':
      return 'premium-promotion-badge';
    case 'standard':
      return 'standard-promotion-badge';
    case 'basic':
      return 'basic-promotion-badge';
    default:
      return 'promotion-badge';
  }
};

/**
 * Check if a product has an active promotion
 * More strict check that considers both promotion type and active status
 * 
 * @param {Object} product - The product object to check
 * @returns {boolean} - True if promotion is active, false otherwise
 */
export const hasActivePromotion = (product) => {
  if (!product) return false;

  // Use backend's is_promotion_active if available (most reliable)
  if (typeof product.is_promotion_active === 'boolean') {
    return product.is_promotion_active && Boolean(product.promote_type);
  }

  // Support alternative boolean flags provided by various endpoints
  if (typeof product.has_active_promotion === 'boolean') {
    return product.has_active_promotion;
  }

  if (typeof product.is_promoted === 'boolean') {
    // When backend supplies is_promoted, trust it directly
    return product.is_promoted;
  }

  // Fallback: Manual expiration check
  if (!product.promote_type || 
      product.promote_type === 'none' || 
      product.promote_type === null || 
      product.promote_type === '') {
    return false;
  }

  // Check expiration dates if available
  if (product.promoted_end_date) {
    const endDate = new Date(product.promoted_end_date);
    const now = new Date();
    
    // Promotion has ended
    if (now > endDate) return false;

    // Check if promotion has started
    if (product.promoted_start_date) {
      const startDate = new Date(product.promoted_start_date);
      if (now < startDate) return false;
    }
  }

  return true;
};

/**
 * Sort products with promoted ones first
 * Maintains relative order within promoted and non-promoted groups
 * 
 * @param {Array} products - Array of product objects to sort
 * @returns {Array} - Sorted array with promoted products first
 */
export const sortPromotedFirst = (products) => {
  if (!Array.isArray(products)) return [];

  const promoted = [];
  const regular = [];

  products.forEach(product => {
    if (hasActivePromotion(product)) {
      promoted.push(product);
    } else {
      regular.push(product);
    }
  });

  return [...promoted, ...regular];
};

/**
 * Get promotion priority for sorting
 * Higher number = higher priority
 * 
 * @param {Object} product - The product object
 * @returns {number} - Priority number for sorting
 */
export const getPromotionPriority = (product) => {
  if (!hasActivePromotion(product)) return 0;

  const promoteType = product.promote_type;
  
  switch (promoteType) {
    case 'premium':
      return 3;
    case 'standard':
      return 2;
    case 'basic':
      return 1;
    default:
      return 1;
  }
};

/**
 * Advanced sort with promotion priority
 * Sorts by promotion priority first, then by other criteria
 * 
 * @param {Array} products - Array of product objects to sort
 * @param {Function} secondarySort - Optional secondary sort function
 * @returns {Array} - Sorted array with highest priority promotions first
 */
export const sortByPromotionPriority = (products, secondarySort = null) => {
  if (!Array.isArray(products)) return [];

  return products.sort((a, b) => {
    const priorityA = getPromotionPriority(a);
    const priorityB = getPromotionPriority(b);

    // Primary sort by promotion priority
    if (priorityA !== priorityB) {
      return priorityB - priorityA; // Higher priority first
    }

    // Secondary sort if provided
    if (secondarySort && typeof secondarySort === 'function') {
      return secondarySort(a, b);
    }

    return 0; // Maintain original order if no secondary sort
  });
};

/**
 * Create promotion-enhanced product object
 * Standardizes promotion data across different API responses
 * 
 * @param {Object} product - Raw product object from API
 * @returns {Object} - Enhanced product object with standardized promotion data
 */
export const enhanceProductWithPromotion = (product) => {
  if (!product) return null;

  const promoted = isProductPromoted(product);
  const activePromotion = hasActivePromotion(product);

  return {
    ...product,
    // Standardized promotion fields
    is_promoted: typeof product.is_promoted === 'boolean' ? product.is_promoted : promoted,
    has_active_promotion: typeof product.has_active_promotion === 'boolean' ? product.has_active_promotion : activePromotion,
    promotion_priority: getPromotionPriority(product),
    promotion_label: getPromotionTypeLabel(product),
    promotion_badge_class: getPromotionBadgeClass(product),
    
    // Ensure consistent promotion data structure
    promote_type: product.promote_type || null,
    is_promotion_active: typeof product.is_promotion_active === 'boolean' ? product.is_promotion_active : promoted,
  };
};

/**
 * Bulk enhance products with promotion data
 * 
 * @param {Array} products - Array of raw product objects
 * @returns {Array} - Array of enhanced product objects
 */
export const enhanceProductsWithPromotion = (products) => {
  if (!Array.isArray(products)) return [];
  
  return products.map(enhanceProductWithPromotion).filter(Boolean);
};

/**
 * Check if product promotion has expired
 * 
 * @param {Object} product - The product object with promotion dates
 * @returns {boolean} - True if promotion has expired, false otherwise
 */
export const isPromotionExpired = (product) => {
  if (!product || !product.promote_type) return false;

  // If backend provides is_promotion_active, use inverse of that (most reliable)
  if (typeof product.is_promotion_active === 'boolean') {
    return product.promote_type && !product.is_promotion_active;
  }

  if (typeof product.has_active_promotion === 'boolean') {
    return product.promote_type && !product.has_active_promotion;
  }

  if (typeof product.is_promoted === 'boolean') {
    return product.promote_type && !product.is_promoted;
  }

  // Manual expiration check - only if we have the end date
  if (product.promoted_end_date) {
    const endDate = new Date(product.promoted_end_date);
    const now = new Date();
    return now > endDate;
  }

  // If we don't have end date info, assume not expired (backend should handle this)
  return false;
};

/**
 * Get promotion time remaining
 * 
 * @param {Object} product - The product object with promotion dates
 * @returns {Object} - Object with time remaining information
 */
export const getPromotionTimeRemaining = (product) => {
  const defaultResult = {
    isExpired: false,
    daysRemaining: 0,
    hoursRemaining: 0,
    humanReadable: 'Durée non définie'
  };

  // First check if promotion is active according to backend
  if (!hasActivePromotion(product)) {
    return {
      ...defaultResult,
      isExpired: true,
      humanReadable: 'Expiré'
    };
  }

  // If we don't have end date, show as active but without specific time
  if (!product.promoted_end_date) {
    return {
      ...defaultResult,
      humanReadable: 'Actif'
    };
  }

  const endDate = new Date(product.promoted_end_date);
  const now = new Date();
  const diffTime = endDate.getTime() - now.getTime();

  if (diffTime <= 0) {
    return {
      ...defaultResult,
      isExpired: true,
      humanReadable: 'Expiré'
    };
  }

  const daysRemaining = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const hoursRemaining = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  let humanReadable = '';
  if (daysRemaining > 0) {
    humanReadable = `${daysRemaining} jour${daysRemaining > 1 ? 's' : ''}`;
  } else if (hoursRemaining > 0) {
    humanReadable = `${hoursRemaining} heure${hoursRemaining > 1 ? 's' : ''}`;
  } else {
    humanReadable = 'Moins d\'une heure';
  }

  return {
    isExpired: false,
    daysRemaining,
    hoursRemaining,
    humanReadable
  };
};

/**
 * Filter products to remove expired promotions
 * 
 * @param {Array} products - Array of product objects
 * @returns {Array} - Array with expired promotions filtered out
 */
export const filterActivePromotions = (products) => {
  if (!Array.isArray(products)) return [];
  
  return products.filter(product => {
    // Keep non-promoted products
    if (!product.promote_type) return true;
    
    // For promoted products, only keep active ones
    return hasActivePromotion(product) && !isPromotionExpired(product);
  });
};

/**
 * Get promotion status with expiration info
 * 
 * @param {Object} product - The product object
 * @returns {Object} - Comprehensive promotion status
 */
export const getPromotionStatus = (product) => {
  if (!product) {
    return {
      isPromoted: false,
      isActive: false,
      isExpired: false,
      timeRemaining: null,
      statusText: 'Non promu'
    };
  }

  const isPromoted = isProductPromoted(product);
  const isActive = hasActivePromotion(product);
  const isExpired = isPromotionExpired(product);
  const timeRemaining = isActive ? getPromotionTimeRemaining(product) : null;

  let statusText = 'Non promu';
  if (isPromoted) {
    if (isActive) {
      statusText = `Promu - ${timeRemaining?.humanReadable || 'Actif'}`;
    } else if (isExpired) {
      statusText = 'Promotion expirée';
    } else {
      statusText = 'Promotion inactive';
    }
  }

  return {
    isPromoted,
    isActive,
    isExpired,
    timeRemaining,
    statusText
  };
};

// Export all utilities as named exports for tree shaking
export default {
  isProductPromoted,
  filterPromotedProducts,
  filterNonPromotedProducts,
  getPromotionTypeLabel,
  getPromotionBadgeClass,
  hasActivePromotion,
  sortPromotedFirst,
  getPromotionPriority,
  sortByPromotionPriority,
  enhanceProductWithPromotion,
  enhanceProductsWithPromotion,
  isPromotionExpired,
  getPromotionTimeRemaining,
  filterActivePromotions,
  getPromotionStatus,
};
